import { FormInputField, FormInputFieldStatus } from '../../../contracts/form-input-field.js';
import { MapUtils } from '../../../utility/map-utils.js';
import { isSameData, isSameList } from '../../dto/data-model-utils.js';
import { LocalizedString } from '../localized-string.js';
import { VersionRange } from '../../core/version-range.js';
import { toSafeFilename } from '../../../utility/string.js';
import { DataModelBuilder, SerializationContext, SerializedData } from '../../dto/dto.js';
import { FormFieldOption } from './form-field-option.js';
import { JSONUtils } from '../../../utility/json-utils.js';
import { fromEnumAsString, toEnumFromString } from '../../../serialization/enum-utils.js';

export class OptionSelectFormInputField extends FormInputField {
  static readonly TYPE_ID: string = '78f5e64b2e564a6292f5af4743f4c54f';
  static readonly SCHEMA_VERSION: number = 4;

  constructor(
    public readonly uniqueId: string,
    public readonly titles: LocalizedString,
    public readonly descriptions: LocalizedString,
    public readonly options: FormFieldOption[],
    public readonly isRequired: boolean,

    // Set to [false] if selecting multiple options is enabled. In this case show
    // radio buttons on the UI.
    // Set to [true] if selecting multiple options is enabled. In this case show
    // check boxes on the UI.
    public readonly isMultiSelect: boolean,
    public readonly status: FormInputFieldStatus,
  ) {
    super();
  }

  get $typeId(): string {
    return OptionSelectFormInputField.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, OptionSelectFormInputField.SCHEMA_VERSION);
  }

  static fromMap(context: SerializationContext, map: SerializedData): OptionSelectFormInputField {
    return new OptionSelectFormInputField(
        MapUtils.getString(map, 'uniqueId'),
        context.fromData<LocalizedString>(MapUtils.get<SerializedData>(map, 'titles')),
        context.fromData<LocalizedString>(MapUtils.get<SerializedData>(map, 'descriptions')),
        MapUtils.getList<FormFieldOption>(map, 'options', (value) =>
          context.fromData<FormFieldOption>(JSONUtils.decodeRecord(value)),
        ),
        MapUtils.getBoolean(map, 'isRequired'),
        MapUtils.get<boolean>(map, 'isMultiSelect'),
        toEnumFromString(MapUtils.getString(map, '$status'), FormInputFieldStatus),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'titles': context.toData(this.titles),
      'descriptions': context.toData(this.descriptions),
      'options': this.options.map((e) => context.toData(e)),
      'isRequired': this.isRequired,
      'isMultiSelect': this.isMultiSelect,
      '$status': fromEnumAsString(this.status),
    };
  }

  clone(): this {
    return new OptionSelectFormInputField(
        this.uniqueId,
        this.titles.clone(),
        this.descriptions.clone(),
        this.options.map((e) => e.clone()),
        this.isRequired,
        this.isMultiSelect,
        this.status,
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
       isSameData(other.titles, this.titles) &&
       isSameData(other.descriptions, this.descriptions) &&
       isSameList(other.options, this.options, isSameData) &&
       other.isRequired === this.isRequired &&
       other.isMultiSelect === this.isMultiSelect &&
       other.status === this.status;
  }
}

export class OptionSelectFormInputFieldBuilder extends DataModelBuilder<OptionSelectFormInputField> {
  build(context: SerializationContext, map: SerializedData): OptionSelectFormInputField {
    return OptionSelectFormInputField.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(OptionSelectFormInputField.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(OptionSelectFormInputField.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version `isRequired` of type `boolean` and `uniqueId` of type `string` was introduced.
    if(version < 2) {
      MapUtils.setIfNullOrUndefined(map, 'isRequired', true);
      MapUtils.setIfNullOrUndefined(map, 'uniqueId', toSafeFilename(
          context.fromData<LocalizedString>(MapUtils.getRecord(map, 'titles')).getAnyOrDefault({ defValue: 'default' }))
      );
      version = 2;
    }

    // In version 3 we changed `options` from `List<LocalizedString>` to `List<FormFieldOption>`.
    if(version < 3) {
      const options = MapUtils.getList<LocalizedString>(map, 'options', (value) =>
        context.fromData<LocalizedString>(JSONUtils.decodeRecord(value)),
      );
      map['options'] = options.map((value) =>
        context.toData(FormFieldOption.create({ uniqueId: toSafeFilename(value.getAnyOrDefault({ defValue: 'default' })), value: value.clone() })),
      );
      version = 3;
    }

    // In version 4 we added `$status` of type `FormInputFieldStatus`.
    if(version < 4) {
      MapUtils.setIfNullOrUndefined(map, '$status', FormInputFieldStatus.Live);
      version = 4;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
